import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { Plus, Loader2, Users, Edit2, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { ConfirmDialog } from './ConfirmDialog';
import {
  OrganizationDetail,
  Team,
  TeamDetail,
  TeamMember,
  listOrgTeams,
  getTeam,
  createTeam,
  updateTeam,
  deleteTeam,
} from '@/services/orgTeamApi';

interface TeamsTabProps {
  organization: OrganizationDetail;
  userRole: string;
  onUpdate: () => void;
}

export function TeamsTab({ organization, userRole, onUpdate }: TeamsTabProps) {
  const { toast } = useToast();

  const isOwner = userRole === 'owner';
  const isAdmin = userRole === 'admin' || isOwner;

  const [isLoading, setIsLoading] = useState(true);
  const [teams, setTeams] = useState<Team[]>([]);
  const [expandedTeamId, setExpandedTeamId] = useState<string | null>(null);
  const [expandedTeamDetails, setExpandedTeamDetails] = useState<TeamDetail | null>(null);

  // Create dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createData, setCreateData] = useState({ name: '', description: '' });
  const [isCreating, setIsCreating] = useState(false);

  // Edit dialog state
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [editData, setEditData] = useState({ name: '', description: '' });
  const [isSaving, setIsSaving] = useState(false);

  // Delete dialog state
  const [deletingTeam, setDeletingTeam] = useState<Team | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load teams
  const loadTeams = useCallback(async () => {
    try {
      const data = await listOrgTeams(organization.id);
      setTeams(data);
    } catch (error) {
      console.error('Failed to load teams:', error);
      toast({
        title: 'Error',
        description: 'Failed to load teams',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [organization.id, toast]);

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  // Handle team expand
  const handleExpandTeam = async (team: Team) => {
    if (expandedTeamId === team.id) {
      setExpandedTeamId(null);
      setExpandedTeamDetails(null);
      return;
    }

    setExpandedTeamId(team.id);
    try {
      const details = await getTeam(team.id);
      setExpandedTeamDetails(details);
    } catch (error) {
      console.error('Failed to load team details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load team members',
        variant: 'destructive',
      });
    }
  };

  // Handle create team
  const handleCreate = async () => {
    if (!createData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Team name is required',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      await createTeam({
        name: createData.name.trim(),
        description: createData.description.trim() || undefined,
        organization_id: organization.id,
      });

      toast({
        title: 'Team created',
        description: `${createData.name} has been created`,
      });

      setShowCreateDialog(false);
      setCreateData({ name: '', description: '' });
      loadTeams();
      onUpdate();
    } catch (error: any) {
      console.error('Failed to create team:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to create team',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  // Handle edit team
  const handleEdit = (team: Team) => {
    setEditingTeam(team);
    setEditData({
      name: team.name,
      description: team.description || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingTeam || !editData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Team name is required',
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);
    try {
      await updateTeam(editingTeam.id, {
        name: editData.name.trim(),
        description: editData.description.trim() || undefined,
      });

      toast({
        title: 'Team updated',
        description: 'Team details have been saved',
      });

      setEditingTeam(null);
      loadTeams();
    } catch (error: any) {
      console.error('Failed to update team:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update team',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete team
  const handleDelete = async () => {
    if (!deletingTeam) return;

    setIsDeleting(true);
    try {
      await deleteTeam(deletingTeam.id);

      toast({
        title: 'Team deleted',
        description: `${deletingTeam.name} has been deleted`,
      });

      setDeletingTeam(null);
      if (expandedTeamId === deletingTeam.id) {
        setExpandedTeamId(null);
        setExpandedTeamDetails(null);
      }
      loadTeams();
      onUpdate();
    } catch (error: any) {
      console.error('Failed to delete team:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to delete team',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Teams</CardTitle>
            <CardDescription>
              Manage teams within this organization
            </CardDescription>
          </div>
          {isAdmin && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Team
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : teams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No teams yet</p>
            {isAdmin && (
              <Button variant="outline" className="mt-4" onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create your first team
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin && <TableHead className="w-24">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.map((team) => (
                <React.Fragment key={team.id}>
                  <TableRow
                    className="cursor-pointer"
                    onClick={() => handleExpandTeam(team)}
                  >
                    <TableCell>
                      {expandedTeamId === team.id ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{team.name}</TableCell>
                    <TableCell>{team.member_count}</TableCell>
                    <TableCell>{formatDate(team.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant={team.is_active ? 'default' : 'secondary'}>
                        {team.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(team);
                            }}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          {isOwner && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeletingTeam(team);
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                  {/* Expanded row showing team members */}
                  {expandedTeamId === team.id && expandedTeamDetails && (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 6 : 5} className="bg-muted/50">
                        <div className="py-2 px-4">
                          {team.description && (
                            <p className="text-sm text-muted-foreground mb-4">{team.description}</p>
                          )}
                          <p className="text-sm font-medium mb-2">Members</p>
                          <div className="flex flex-wrap gap-2">
                            {expandedTeamDetails.members.map((member) => (
                              <Badge key={member.user_id} variant="outline">
                                {member.display_name || member.username}
                                <span className="ml-1 text-muted-foreground">({member.role})</span>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Create Team Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Team</DialogTitle>
            <DialogDescription>
              Create a new team within {organization.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                value={createData.name}
                onChange={(e) => setCreateData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Engineering"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-description">Description (optional)</Label>
              <Textarea
                id="team-description"
                value={createData.description}
                onChange={(e) => setCreateData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="A brief description of the team"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} disabled={isCreating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isCreating}>
              {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={!!editingTeam} onOpenChange={(open) => !open && setEditingTeam(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
            <DialogDescription>
              Update team details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-team-name">Team Name</Label>
              <Input
                id="edit-team-name"
                value={editData.name}
                onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-team-description">Description (optional)</Label>
              <Textarea
                id="edit-team-description"
                value={editData.description}
                onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTeam(null)} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deletingTeam}
        onOpenChange={(open) => !open && setDeletingTeam(null)}
        title="Delete Team"
        description={`Are you sure you want to delete "${deletingTeam?.name}"? This action cannot be undone and all team members will lose access.`}
        confirmLabel="Delete"
        variant="destructive"
        isLoading={isDeleting}
        onConfirm={handleDelete}
      />
    </Card>
  );
}
